import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { isAxiosError } from 'axios';
import { firebaseAuth } from '@config/firebase';
import { apiClient } from '@api/client';
import { authClient } from '@api/authClient';
import { authLog } from '@utils/authLog';
import {
  backendLoginResponseSchema,
  meResponseSchema,
  type AuthUser,
  type BackendUserData,
  type LoginFormValues,
  type MeData,
} from './schema';

const BACKEND_LOGIN_PATH = '/user/login';
const APP_TYPE = 'adminPanel';

/**
 * `signInWithCustomToken` causes Firebase to briefly sign the previous user
 * out before signing in with the custom token. That fires `onAuthStateChanged`
 * with `null`, which would otherwise wipe our Redux session mid-flow and
 * bounce the user back to `/login`. We flip this flag for the duration of
 * `loginWithEmail` so the subscription can ignore transient signouts.
 */
let loginInFlight = false;
export const isLoginInFlight = () => loginInFlight;

const toAuthUser = (u: User): AuthUser => ({
  uid: u.uid,
  email: u.email,
  displayName: u.displayName,
  photoURL: u.photoURL,
});

/**
 * Distinguishes which step of the multi-step login failed so the UI can
 * show the right message and so we know whether to roll back Firebase.
 */
export type AuthErrorStage = 'firebase' | 'backend' | 'custom-token';

export class AuthError extends Error {
  constructor(
    public stage: AuthErrorStage,
    public original: unknown,
    message?: string,
  ) {
    super(message ?? extractMessage(original));
    this.name = 'AuthError';
  }
}

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

/**
 * Multi-step sign-in:
 *   1. Firebase signInWithEmailAndPassword       → initial ID token
 *   2. POST /user/login (Authorization: idToken) → custom token + user data
 *   3. Firebase signInWithCustomToken            → final ID token
 *   4. GET /me on commodity-mdm-api              → role codes
 *
 * If any step after Firebase email/password fails, we roll back the Firebase
 * session via signOut() so the user is never left half-authenticated.
 *
 * Step 4 is what enforces the admin-panel allowlist: a Firebase identity that
 * passes auth-api but holds no admin role on the master DB (e.g. a mobile-only
 * account that snuck through) gets rejected here with a 401.
 */
export async function loginWithEmail({ email, password }: LoginFormValues): Promise<{
  user: AuthUser;
  token: string;
  profile: BackendUserData;
  me: MeData;
}> {
  loginInFlight = true;
  try {
    // Step 1 — Firebase email/password
    let initialIdToken: string;
    try {
      const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      initialIdToken = await credential.user.getIdToken();
    } catch (err) {
      throw new AuthError('firebase', err);
    }

    try {
      // Step 2 — exchange ID token for a custom token via the backend
      const response = await authClient.post(
        BACKEND_LOGIN_PATH,
        { appType: APP_TYPE },
        { headers: { Authorization: `Bearer ${initialIdToken}` } },
      );

      // Defensive: backend may return 200 with `success: false` and a message
      // (e.g. account not allowed for adminPanel). Surface that before schema parse.
      const wrapper = (response.data as { data?: { success?: boolean; message?: string } })
        ?.data;
      if (wrapper && wrapper.success === false) {
        throw new AuthError(
          'backend',
          new Error(wrapper.message ?? 'Sign-in rejected by server.'),
          wrapper.message ?? 'Sign-in rejected by server.',
        );
      }

      const parsed = backendLoginResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new AuthError('backend', parsed.error, 'Unexpected response from server.');
      }
      const { token: customToken, userData } = parsed.data.data;

      // Step 3 — re-authenticate with the custom token to bind the backend session
      let finalToken: string;
      let finalUser: User;
      try {
        const customCredential = await signInWithCustomToken(firebaseAuth, customToken);
        finalUser = customCredential.user;
        finalToken = await customCredential.user.getIdToken();
      } catch (err) {
        throw new AuthError('custom-token', err);
      }

      // Step 4 — load admin-panel identity (incl. roles). Catches the
      // "valid Firebase user but no admin role" case the auth-api can't see.
      let me: MeData;
      try {
        me = await loadMe();
      } catch (err) {
        const message = extractMeErrorMessage(err);
        throw new AuthError('backend', err, message);
      }

      return { user: toAuthUser(finalUser), token: finalToken, profile: userData, me };
    } catch (err) {
      // CRITICAL: roll back the partial Firebase session so the user is not left
      // half-signed-in. Swallow signOut errors — the original failure is what matters.
      await signOut(firebaseAuth).catch(() => {});

      if (err instanceof AuthError) throw err;
      throw new AuthError('backend', err);
    }
  } finally {
    loginInFlight = false;
  }
}

export async function logout(): Promise<void> {
  await signOut(firebaseAuth);
}

/**
 * Fetches the current user's identity + role codes from commodity-mdm-api.
 * Called after every fresh session (login + boot rehydrate) so the UI can
 * drive role-based nav + route guards without trusting only the Firebase token.
 *
 * The Bearer token is attached by `apiClient`'s request interceptor.
 */
export async function loadMe(): Promise<MeData> {
  const response = await apiClient.get('/me');
  const parsed = meResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new AuthError(
      'backend',
      parsed.error,
      'Unexpected response while loading your profile.',
    );
  }
  return parsed.data.data;
}

function extractMeErrorMessage(err: unknown): string {
  if (isAxiosError(err) && err.response?.status === 401) {
    const data = err.response.data as { message?: string } | undefined;
    return (
      data?.message?.trim() ||
      'Your account is not authorized to access the admin panel.'
    );
  }
  return extractMessage(err);
}

export function subscribeToAuthChanges(
  cb: (user: AuthUser | null, token: string | null) => void,
) {
  return onAuthStateChanged(firebaseAuth, async (u) => {
    if (!u) {
      authLog('auth-state-change', { uid: null, hasToken: false });
      cb(null, null);
      return;
    }
    try {
      const token = await u.getIdToken();
      authLog('auth-state-change', { uid: u.uid, hasToken: true });
      cb(toAuthUser(u), token);
    } catch (err) {
      // Transient: SDK still has the user but token fetch failed (network blip,
      // refresh race). The hook's safety net checks `firebaseAuth.currentUser`
      // and decides whether to keep the session.
      authLog('auth-state-change', {
        uid: u.uid,
        hasToken: false,
        getIdTokenError: err instanceof Error ? err.message : String(err),
      });
      cb(null, null);
    }
  });
}

/**
 * Resolves once Firebase has loaded its persisted auth state from storage.
 * Use this on app boot before deciding whether the user is signed in,
 * to avoid the transient `null` window during persistence rehydration.
 */
export async function waitForAuthReady(): Promise<AuthUser | null> {
  await firebaseAuth.authStateReady();
  const u = firebaseAuth.currentUser;
  authLog('auth-ready', { uid: u?.uid ?? null });
  return u ? toAuthUser(u) : null;
}

// ────────────────────────────────────────────────────────────────────────────
// Friendly error mapping
// ────────────────────────────────────────────────────────────────────────────

export function mapAuthError(err: unknown): string {
  if (err instanceof AuthError) {
    if (err.stage === 'firebase') return mapFirebaseCode(err.original);
    if (err.stage === 'backend') {
      // If we attached an explicit message (e.g. backend's own `message`), prefer it.
      const fromBackend = mapBackendError(err.original);
      const explicit = err.message && err.message !== extractMessage(err.original);
      return explicit ? err.message : fromBackend;
    }
    if (err.stage === 'custom-token') return 'Could not establish your session. Please try again.';
  }
  return mapFirebaseCode(err);
}

function mapFirebaseCode(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    default:
      return 'Sign-in failed. Please try again.';
  }
}

function mapBackendError(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const serverMsg =
      (err.response?.data as { message?: string } | undefined)?.message?.trim();

    if (status === 401 || status === 403) {
      return serverMsg || 'You are not authorized to access this application.';
    }
    if (status && status >= 500) {
      return 'Our servers are having trouble right now. Please try again shortly.';
    }
    if (err.code === 'ERR_NETWORK') {
      return 'Could not reach the server. Check your connection and try again.';
    }
    return serverMsg || 'Sign-in failed. Please try again.';
  }
  return 'Sign-in failed. Please try again.';
}

